type RunStartPayload = {
	nickname: string
	level_index: number
	map_key: string
	build_version?: string
	client_started_at_ms: number
}

type RunStartResponse = {
	run_id: string
	public_run_code: string
	run_token: string
	server_time_ms: number
	expires_at?: string
}

type RunEventPayload = {
	run_id: string
	seq: number
	event_type: string
	event_time_ms: number
	payload?: Record<string, unknown>
	idempotency_key?: string
}

type RunFinishPayload = {
	run_id: string
	seq_final: number
	end_reason: 'game_over' | 'quit' | 'disconnect' | 'timeout' | 'finished_level'
	client_ended_at_ms: number
	final_budget?: number
	final_max_budget?: number
	summary?: Record<string, unknown>
	tz?: string
}

type RunFinishResponse = {
	run_id: string
	computed_score: number
	integrity_status: string
	flags?: string[]
	daily_position?: number | null
}

type DailyRankingRow = {
	position: number
	run_id: string
	public_run_code: string
	nickname: string
	score: number
	duration_ms: number
	kills_total: number
	coins_total: number
	ended_at?: string
}

type DailyRankingResponse = {
	date: string
	tz: string
	count: number
	ranking: DailyRankingRow[]
}

type MyRankingResponse = {
	run_id: string
	date: string
	tz: string
	position: number | null
	total: number
	score: number
	integrity_status: string
}

type BatchSessionPayload = {
	local_session_id: string
	run_id?: string
	run_token?: string
	start?: RunStartPayload
	events?: Array<Omit<RunEventPayload, 'run_id'>>
	finish?: Omit<RunFinishPayload, 'run_id'>
}

type BatchSessionResult = {
	local_session_id: string
	status: 'ok' | 'error'
	message?: string
	run_id?: string
	run_token?: string
	public_run_code?: string
	events?: Array<{ seq: number | null, status: string, message?: string }>
	finish?: { status: string, message?: string, run_id?: string, computed_score?: number, integrity_status?: string, flags?: string[], daily_position?: number | null }
}

type BatchSyncResponse = {
	server_time_ms: number
	results: BatchSessionResult[]
}

const REQUEST_TIMEOUT_MS = 8000
const HEALTH_CHECK_TIMEOUT_MS = 4000

export default class RankingApiClient
{
	private baseUrl: string

	constructor(baseUrl: string)
	{
		this.baseUrl = baseUrl.replace(/\/+$/, '')
	}

	async checkHealth(timeoutMs: number = HEALTH_CHECK_TIMEOUT_MS): Promise<boolean>
	{
		const controller = new AbortController()
		const timeout = window.setTimeout(() => {
			controller.abort()
		}, timeoutMs)

		try
		{
			const response = await fetch(`${this.baseUrl}/health`, {
				method: 'GET',
				headers: { 'Accept': 'application/json' },
				signal: controller.signal
			})

			if (!response.ok)
			{
				return false
			}

			// Un dev server sin backend puede devolver 200 con el HTML del SPA como fallback.
			const contentType = response.headers.get('content-type') ?? ''
			if (!contentType.toLowerCase().includes('application/json'))
			{
				return false
			}

			const body = await response.json() as { status?: unknown }
			return body?.status === 'ok'
		}
		catch
		{
			return false
		}
		finally
		{
			window.clearTimeout(timeout)
		}
	}

	async syncBatch(sessions: BatchSessionPayload[]): Promise<BatchSyncResponse>
	{
		return this.request<BatchSyncResponse>('/runs/sync-batch', {
			method: 'POST',
			body: { sessions }
		})
	}

	async startRun(payload: RunStartPayload): Promise<RunStartResponse>
	{
		return this.request<RunStartResponse>('/runs/start', {
			method: 'POST',
			body: payload
		})
	}

	async sendEvent(payload: RunEventPayload, token: string): Promise<void>
	{
		await this.request('/runs/event', {
			method: 'POST',
			body: payload,
			token
		})
	}

	async finishRun(payload: RunFinishPayload, token: string): Promise<RunFinishResponse>
	{
		return this.request<RunFinishResponse>('/runs/finish', {
			method: 'POST',
			body: payload,
			token
		})
	}

	async getDailyRanking(date: string, tz: string, limit: number): Promise<DailyRankingResponse>
	{
		const search = new URLSearchParams({
			date,
			tz,
			limit: `${limit}`
		})
		return this.request<DailyRankingResponse>(`/rankings/daily?${search.toString()}`, {
			method: 'GET'
		})
	}

	async getMyRanking(runId: string, tz: string): Promise<MyRankingResponse>
	{
		const search = new URLSearchParams({
			run_id: runId,
			tz
		})
		return this.request<MyRankingResponse>(`/rankings/me?${search.toString()}`, {
			method: 'GET'
		})
	}

	private async request<TResponse>(
		path: string,
		options: {
			method: 'GET' | 'POST'
			body?: unknown
			token?: string
		}
	): Promise<TResponse>
	{
		const controller = new AbortController()
		const timeout = window.setTimeout(() => {
			controller.abort()
		}, REQUEST_TIMEOUT_MS)

		try
		{
			const headers: Record<string, string> = {
				'Accept': 'application/json'
			}

			if (options.method !== 'GET')
			{
				headers['Content-Type'] = 'application/json'
			}

			if (options.token)
			{
				headers['Authorization'] = `Bearer ${options.token}`
			}

			const response = await fetch(`${this.baseUrl}${path}`, {
				method: options.method,
				headers,
				body: options.body === undefined ? undefined : JSON.stringify(options.body),
				signal: controller.signal
			})

			if (!response.ok)
			{
				let details: unknown
				try
				{
					details = await response.json()
				}
				catch
				{
					details = await response.text()
				}

				throw new Error(`Ranking API ${response.status}: ${JSON.stringify(details)}`)
			}

			if (response.status === 204)
			{
				return undefined as TResponse
			}

			return await response.json() as TResponse
		}
		finally
		{
			window.clearTimeout(timeout)
		}
	}
}

export type {
	RunStartResponse,
	RunFinishResponse,
	DailyRankingResponse,
	MyRankingResponse,
	DailyRankingRow,
	RunStartPayload,
	RunEventPayload,
	RunFinishPayload,
	BatchSessionPayload,
	BatchSessionResult,
	BatchSyncResponse
}
